import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { useGetExperience } from "@src/api/experiences/get-experience";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import {
  EXPERIMENT_ROUTES,
  FALLBACK_TITLE,
  PERSONALIZATION_ROUTES,
  PUBLIC_ROUTES,
  ROUTE_TITLES,
} from "@src/routes/routes";

// Detail routes whose :id is an experience-backed entity — experiment,
// experience and personalization all load via useGetExperience(id).
const EXPERIENCE_DETAIL_PATTERNS = [
  EXPERIMENT_ROUTES.Experiment_Id,
  PERSONALIZATION_ROUTES.Personalization_Id,
];

const specificity = (pattern: string) => pattern.split("/").length;

/** Title declared in ROUTE_TITLES for the current path (most specific match). */
const matchedTitle = (pathname: string): string | undefined => {
  // Fast path: exact key for static routes (no params).
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  // Otherwise match dynamic patterns, preferring the most specific one.
  const bestPattern = Object.keys(ROUTE_TITLES)
    .filter((pattern) => matchPath(pattern, pathname))
    .sort((a, b) => specificity(b) - specificity(a))[0];

  return bestPattern ? ROUTE_TITLES[bestPattern] : undefined;
};

/**
 * Sets `document.title` from ROUTE_TITLES (colocated in routes.ts), falling
 * back to FALLBACK_TITLE. Experience/experiment/personalization detail pages
 * show the live entity name (reused from the page's own cached query).
 * Prefixes `${org} | ` when the user can access multiple orgs.
 * Mount once (LoggedInProvider).
 */
export const useDocumentTitle = () => {
  const { pathname } = useLocation();

  const experienceId = EXPERIENCE_DETAIL_PATTERNS.map((pattern) =>
    matchPath(pattern, pathname)
  ).find(Boolean)?.params.id;
  const experience = useGetExperience(experienceId);
  const experienceName = experience.data?.experience?.name;

  const org = useGetOrg();
  const orgList = useGetOrgList();
  const hasMultipleOrgs = (orgList.data?.orgs?.length ?? 0) > 1;
  const orgName = org.data?.org?.name;

  useEffect(() => {
    const route = experienceName ?? matchedTitle(pathname) ?? FALLBACK_TITLE;
    document.title =
      hasMultipleOrgs && orgName ? `${orgName} | ${route}` : route;
  }, [pathname, experienceName, hasMultipleOrgs, orgName]);
};

/**
 * Pre-auth variant for the public shell. The shell only ever renders the
 * login experience (the catch-all route serves LoginPage for any
 * unauthenticated path), so the title is always the login title.
 */
export const usePublicDocumentTitle = () => {
  useEffect(() => {
    document.title = ROUTE_TITLES[PUBLIC_ROUTES.Login] ?? FALLBACK_TITLE;
  }, []);
};
